import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Organization } from '@/api/organization/organizationApi.types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phoneInput';
import { AssistantTargetRegion } from '@/components/assistant/uiActions/AssistantTargetRegion';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { useUpdateOrganizationMutation } from '@/hooks/useOrganization';
import { isBlankOrValidPhoneNumber } from '@/utils/phoneNumbers';

const MINIMUM_CANCELLATION_CUTOFF_HOURS = 0;
const MINIMUM_CLIENT_RECORD_RETENTION_YEARS = 1;
const MAXIMUM_CLIENT_RECORD_RETENTION_YEARS = 30;
const MAXIMUM_CANCELLATION_CUTOFF_HOURS = 720;

interface OrganizationFormProps {
  organization: Organization;
}

interface EditableOrganization {
  name: string;
  defaultLanguage: string;
  businessName: string;
  legalName: string;
  taxIdentifier: string;
  registrationNumber: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  countryCode: string;
  contactEmail: string;
  contactPhone: string;
  contactWebsite: string;
  clientCancellationCutoffHours: string;
  clientRecordRetentionYears: string;
}

function toInputValue(value: string | null): string {
  return value === null ? '' : value;
}

function toCutoffHours(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : null;
}

function toNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export function OrganizationForm({ organization }: OrganizationFormProps) {
  const { t } = useTranslation('settings');
  const updateOrganizationMutation = useUpdateOrganizationMutation();
  const [form, setForm] = useState<EditableOrganization>({
    name: organization.name,
    defaultLanguage: organization.defaultLanguage,
    businessName: toInputValue(organization.businessName),
    legalName: toInputValue(organization.legalName),
    taxIdentifier: toInputValue(organization.taxIdentifier),
    registrationNumber: toInputValue(organization.registrationNumber),
    addressLine1: toInputValue(organization.addressLine1),
    addressLine2: toInputValue(organization.addressLine2),
    postalCode: toInputValue(organization.postalCode),
    city: toInputValue(organization.city),
    countryCode: toInputValue(organization.countryCode),
    contactEmail: toInputValue(organization.contactEmail),
    contactPhone: toInputValue(organization.contactPhone),
    contactWebsite: toInputValue(organization.contactWebsite),
    clientCancellationCutoffHours: String(organization.clientCancellationCutoffHours),
    clientRecordRetentionYears: String(organization.clientRecordRetentionYears),
  });

  const [isPhoneValidationVisible, setIsPhoneValidationVisible] = useState(false);

  function updateField(field: keyof EditableOrganization) {
    return function applyChange(changeEvent: ChangeEvent<HTMLInputElement>) {
      const nextValue = changeEvent.target.value;
      setForm(function mergeField(previous) {
        return { ...previous, [field]: nextValue };
      });
    };
  }

  function updateContactPhone(nextValue: string) {
    setForm(function mergeField(previous) {
      return { ...previous, contactPhone: nextValue };
    });
  }

  async function submitOrganization(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (isBlankOrValidPhoneNumber(form.contactPhone) === false) {
      setIsPhoneValidationVisible(true);
      return;
    }
    try {
      await updateOrganizationMutation.mutateAsync({
        name: form.name.trim(),
        defaultLanguage: form.defaultLanguage.trim(),
        businessName: toNullable(form.businessName),
        legalName: toNullable(form.legalName),
        taxIdentifier: toNullable(form.taxIdentifier),
        registrationNumber: toNullable(form.registrationNumber),
        addressLine1: toNullable(form.addressLine1),
        addressLine2: toNullable(form.addressLine2),
        postalCode: toNullable(form.postalCode),
        city: toNullable(form.city),
        countryCode: toNullable(form.countryCode),
        contactEmail: toNullable(form.contactEmail),
        contactPhone: toNullable(form.contactPhone),
        contactWebsite: toNullable(form.contactWebsite),
        clientCancellationCutoffHours: toCutoffHours(form.clientCancellationCutoffHours),
        clientRecordRetentionYears: toCutoffHours(form.clientRecordRetentionYears),
      });
      showSuccessNotification(t('organization.saved'));
    } catch (updateError) {
      const backendMessage = resolveApiErrorMessage(updateError);
      showErrorNotification(
        backendMessage.length > 0 ? backendMessage : t('organization.saveError')
      );
    }
  }

  return (
    <form onSubmit={submitOrganization} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card padding="lg">
          <h2 className="text-sm font-bold text-zooindigo">
            {t('organization.sections.identity')}
          </h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              id="organization-name"
              label={t('organization.fields.name')}
              value={form.name}
              onChange={updateField('name')}
            />
            <TextField
              id="organization-legal-name"
              label={t('organization.fields.legalName')}
              value={form.legalName}
              onChange={updateField('legalName')}
            />
            <TextField
              id="organization-business-name"
              label={t('organization.fields.businessName')}
              value={form.businessName}
              onChange={updateField('businessName')}
            />
            <TextField
              id="organization-language"
              label={t('organization.fields.defaultLanguage')}
              value={form.defaultLanguage}
              onChange={updateField('defaultLanguage')}
            />
            <ReadOnlyField
              id="organization-type"
              label={t('organization.fields.type')}
              value={organization.type}
              hint={t('organization.readonlyHint')}
            />
            <ReadOnlyField
              id="organization-code"
              label={t('organization.fields.code')}
              value={organization.code}
              hint={t('organization.readonlyHint')}
            />
          </div>
        </Card>

        <Card padding="lg">
          <h2 className="text-sm font-bold text-zooindigo">
            {t('organization.sections.registration')}
          </h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              id="organization-tax"
              label={t('organization.fields.taxIdentifier')}
              value={form.taxIdentifier}
              onChange={updateField('taxIdentifier')}
            />
            <TextField
              id="organization-registration"
              label={t('organization.fields.registrationNumber')}
              value={form.registrationNumber}
              onChange={updateField('registrationNumber')}
            />
          </div>
        </Card>

        <Card padding="lg">
          <h2 className="text-sm font-bold text-zooindigo">{t('organization.sections.address')}</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              id="organization-address1"
              label={t('organization.fields.addressLine1')}
              value={form.addressLine1}
              onChange={updateField('addressLine1')}
            />
            <TextField
              id="organization-address2"
              label={t('organization.fields.addressLine2')}
              value={form.addressLine2}
              onChange={updateField('addressLine2')}
            />
            <TextField
              id="organization-postal"
              label={t('organization.fields.postalCode')}
              value={form.postalCode}
              onChange={updateField('postalCode')}
            />
            <TextField
              id="organization-city"
              label={t('organization.fields.city')}
              value={form.city}
              onChange={updateField('city')}
            />
            <TextField
              id="organization-country"
              label={t('organization.fields.countryCode')}
              value={form.countryCode}
              onChange={updateField('countryCode')}
            />
          </div>
        </Card>

        <Card padding="lg">
          <h2 className="text-sm font-bold text-zooindigo">{t('organization.sections.contact')}</h2>
          <div className="mt-4 grid grid-cols-1 gap-4">
            <TextField
              id="organization-contact-email"
              label={t('organization.fields.contactEmail')}
              value={form.contactEmail}
              onChange={updateField('contactEmail')}
            />
            <div className="space-y-1.5">
              <Label htmlFor="organization-contact-phone">
                {t('organization.fields.contactPhone')}
              </Label>
              <PhoneInput
                inputId="organization-contact-phone"
                value={form.contactPhone}
                isValidationVisible={isPhoneValidationVisible}
                onChange={updateContactPhone}
              />
            </div>
            <TextField
              id="organization-contact-website"
              label={t('organization.fields.contactWebsite')}
              value={form.contactWebsite}
              onChange={updateField('contactWebsite')}
            />
          </div>
        </Card>

        <Card padding="lg">
          <h2 className="text-sm font-bold text-zooindigo">
            {t('organization.sections.clientPortal')}
          </h2>
          <div className="mt-4 space-y-1.5 max-w-xs">
            <Label htmlFor="organization-client-cancellation-cutoff">
              {t('organization.fields.clientCancellationCutoffHours')}
            </Label>
            <Input
              id="organization-client-cancellation-cutoff"
              type="number"
              min={MINIMUM_CANCELLATION_CUTOFF_HOURS}
              max={MAXIMUM_CANCELLATION_CUTOFF_HOURS}
              step={1}
              value={form.clientCancellationCutoffHours}
              onChange={updateField('clientCancellationCutoffHours')}
            />
            <p className="text-[11px] text-zoomuted">
              {t('organization.clientCancellationCutoffHint')}
            </p>
          </div>
        </Card>

        <Card padding="lg">
          <h2 className="text-sm font-bold text-zooindigo">
            {t('organization.sections.dataRetention')}
          </h2>
          <AssistantTargetRegion
            targetKey={ASSISTANT_TARGET_KEY.organizationRetentionField}
            className="mt-4 space-y-1.5 max-w-xs"
          >
            <Label htmlFor="organization-client-record-retention">
              {t('organization.fields.clientRecordRetentionYears')}
            </Label>
            <Input
              id="organization-client-record-retention"
              type="number"
              min={MINIMUM_CLIENT_RECORD_RETENTION_YEARS}
              max={MAXIMUM_CLIENT_RECORD_RETENTION_YEARS}
              step={1}
              value={form.clientRecordRetentionYears}
              onChange={updateField('clientRecordRetentionYears')}
            />
            <p className="text-[11px] text-zoomuted">
              {t('organization.clientRecordRetentionHint')}
            </p>
          </AssistantTargetRegion>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button type="submit" isLoading={updateOrganizationMutation.isPending}>
          {t('organization.save')}
        </Button>
      </div>
    </form>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (changeEvent: ChangeEvent<HTMLInputElement>) => void;
}

function TextField({ id, label, value, onChange }: TextFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={onChange} />
    </div>
  );
}

interface ReadOnlyFieldProps {
  id: string;
  label: string;
  value: string;
  hint: string;
}

function ReadOnlyField({ id, label, value, hint }: ReadOnlyFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} disabled readOnly />
      <p className="text-[11px] text-zoomuted">{hint}</p>
    </div>
  );
}
